import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listCustomers, type CustomerListItem } from '@/lib/api/customers.js';
import { getCurrentStore } from '@/lib/api/stores.js';
import { CustomerStatusBadge } from '@/components/common/CustomerStatusBadge.js';

function customerDisplayName(c: Pick<CustomerListItem, 'firstName' | 'lastName'>): string {
  return [c.firstName, c.lastName].filter(Boolean).join(' ');
}

export type CustomerSearchSelectResult = {
  id: string;
  name: string;
  phone?: string | null;
  email?: string | null;
};

export function CustomerSearchSelect({
  selectedId,
  selectedName,
  excludeCustomerId,
  onSelect,
  onClear,
  placeholder = 'Search by name, phone, or email',
  selectedSubtitle = 'Linked customer referral',
}: {
  selectedId?: string | null;
  selectedName?: string | null;
  excludeCustomerId?: string | null;
  onSelect: (customer: CustomerSearchSelectResult) => void;
  onClear: () => void;
  placeholder?: string;
  /** Hint shown under the selected customer name. */
  selectedSubtitle?: string;
}) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  const query = useQuery({
    queryKey: ['customer-search-select', debounced],
    queryFn: () => listCustomers({ q: debounced, page: 1, pageSize: 8 }),
    enabled: debounced.length >= 2,
  });
  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });

  const results = (query.data?.data ?? []).filter((c) => c.id !== excludeCustomerId);

  if (selectedId) {
    return (
      <div className="mt-1 flex items-center justify-between gap-3 rounded-xl border border-slate-300 bg-slate-50 px-3 py-2">
        <div>
          <p className="text-sm font-semibold text-slate-950">{selectedName || 'Selected customer'}</p>
          <p className="text-xs text-slate-500">{selectedSubtitle}</p>
        </div>
        <button
          type="button"
          className="text-sm font-semibold text-slate-600 hover:text-slate-950"
          onClick={() => {
            onClear();
            setSearch('');
          }}
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="mt-1 space-y-2">
      <input
        type="search"
        className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault();
        }}
        placeholder={placeholder}
      />
      {search.trim().length > 0 && search.trim().length < 2 ? (
        <p className="text-sm text-slate-500">Type at least 2 characters to search.</p>
      ) : null}
      {query.isFetching ? <p className="text-sm text-slate-500">Searching…</p> : null}
      {query.isError ? (
        <p className="text-sm text-red-600">Could not load customers. Try again.</p>
      ) : null}
      {debounced.length >= 2 && !query.isFetching && results.length === 0 ? (
        <p className="text-sm text-slate-500">No customers found.</p>
      ) : null}
      {results.length > 0 ? (
        <ul className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {results.map((c) => {
            const name = customerDisplayName(c);
            return (
              <li key={c.id}>
                <button
                  type="button"
                  className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-slate-50"
                  onClick={() => {
                    onSelect({
                      id: c.id,
                      name,
                      ...(c.phone != null ? { phone: c.phone } : {}),
                      ...(c.email != null ? { email: c.email } : {}),
                    });
                    setSearch('');
                  }}
                >
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-950">
                    {name}
                    <CustomerStatusBadge
                      status={c.customerStatus}
                      vipLabel={storeQuery.data?.vipLabel ?? null}
                      flaggedLabel={storeQuery.data?.flaggedLabel ?? null}
                      reason={c.statusReason ?? null}
                    />
                  </span>
                  <span className="text-xs text-slate-500">
                    {[c.phone, c.email].filter(Boolean).join(' · ') || 'No contact info'}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
